import { createQueryKeys } from "@lukemorales/query-key-factory";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { api } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type Profile } from "../types";

export const profile = createQueryKeys("admin-profile", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number | "me") => [id],
});

const include = "user.position";

export type ProfileList = Paginate<Profile>;

export const fetchProfileHistoriesQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...profile.all(page, search),
    queryFn: () =>
      api
        .get<ProfileList>(`profile/${status}`, {
          params: {
            include,
            page,
            ...search,
          },
        })
        .then((response) => response.data),
  });

export const useFetchProfileHistories = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchProfileHistoriesQueryOptions(page, search));

export const fetchProfileQueryOptions = (id?: number | "me") =>
  queryOptions({
    ...profile.one(id),
    queryFn: () =>
      api
        .get<Profile<true>>(
          id === "me" ? `profile/me` : `profile/users/${id}`,
          {
            params: { include },
          },
        )
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchProfile = (id?: number | "me") =>
  useQuery(fetchProfileQueryOptions(id));

export interface ProfileCreateRequest {
  elements: {
    id: number;
    value: any;
  }[];
}

export const useCreateProfileMe = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: ProfileCreateRequest) =>
      api.put("profile/me", data).then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: profile.all._def,
      });
      queryClient.invalidateQueries(profile.one("me"));
    },
  });
};
